import { saveEvent, countOwnEvents, deleteEvent, calendarTeamId, importEvents, listEvents } from '../../extensions/bundled/calendar/calendarStore';
import { requireLocalCapacityFor } from '../../plans/localGate';
import { setUpgradeModalOpen, setUpgradeReason } from '../../store/uiModalStore';
import { saveOrMoveEvent } from '../../extensions/bundled/calendar/calendarMove';
import { parseIcs, buildIcs } from '../../utils/ics';
import { useNotificationStore } from '../../store/notificationStore';
import {
  cancelAppointment,
  createInvitations,
  updateAppointment,
  buildSnapshotFromEvent,
  surfaceInviteError,
  invitesBlockedFor,
  respondToInvitation,
} from '../../services/calendarInvitesService';
import { bumpCalendarEvents } from '../../extensions/bundled/calendar/calendarUiStore';
import { addDays, startOfDay, toDateKey, dayAtMinutes, minutesOfDay } from '../../utils/calendarDates';
import { isReadOnlyEvent, pickIcsText, saveIcsText } from './calendarViewHelpers';

/** Calendar event actions: move / resize, duplicate, edits to a series or one occurrence, delete, invitations, ICS import and export, quick create. */
export function useCalendarEventActions({
  t,
  teams,
  events,
  isAuthenticated,
  currentUser,
  setPopover,
  editorDraft,
  setEditorDraft,
  deleteTarget,
  setDeleteTarget,
  setImportReport,
  icsBusy,
  setIcsBusy,
  setScopeDialog,
}) {
  const handleUpdateEventTimesGuarded = async (event, times) => {
    // A pending-invite ghost is not the viewer's record — it can't be moved.
    if (event.pending_invite) return;
    // Viewer on a team calendar: read-only (server would reject the push —
    // mirror it, don't fake it).
    if (isReadOnlyEvent(event, teams)) return;
    // Dragging an OCCURRENCE = implicit "this event" override, no dialog
    // (PLAN.recurrence §3 — consistent with the 15-min-snap flow). Dragging
    // an override record is a normal move.
    if (event.is_occurrence && !event.override_of) {
      await writeOverride(event, {
        ...event,
        all_day: false,
        start: times.start.toISOString(),
        end: times.end.toISOString(),
        start_date: null,
        end_date: null,
      });
      return;
    }
    await handleUpdateEventTimes(event, times);
  };

  const handleDuplicateEvent = async (event) => {
    // An occurrence duplicates as a SINGLE event with its own times — never a
    // second master series (the instance carries the master's rule).
    const base = event.is_occurrence
      ? { ...event, recurrence: null, exdates: [], is_occurrence: undefined, occurrence_date: undefined }
      : event;
    if ((base.calendar == null || base.calendar === 'personal') && !(await requireLocalCapacityFor('calendarEvents', countOwnEvents))) return;
    await saveEvent({ ...base, uid: null, created_at: null, override_of: null });
    bumpCalendarEvents();
  };

  /* ── Phase 4 recurrence semantics (PLAN.recurrence §3) ─────────────────── */

  const masterOf = (occurrence) =>
    events.find((e) => e.uid === occurrence.uid && e.recurrence) || null;
  const overridesOf = (master) =>
    master?.recurrence ? events.filter((e) => e.override_of?.master_uid === master.uid) : [];

  /** Save an edit — a switched calendar is a MOVE (calendarMove.js, card
   *  fbb1b6c8). A refused write keeps the original: error toast, null. */
  const persistEdit = (previous, fields) =>
    saveOrMoveEvent(previous, fields, overridesOf(previous)).catch((err) => {
      // Plans v2 card 3.3: an existing team event moved into the personal
      // calendar past Free's cap is a NEW personal event; the store refuses
      // it (calendarStore.saveEvent) and the upgrade opens, never a bare
      // "save failed".
      if (err?.planLimit) {
        setUpgradeReason(err.message);
        setUpgradeModalOpen(true);
        return null;
      }
      useNotificationStore.getState().error(String(err?.message || err), t('calendar.toast.saveFailed'));
      return null;
    });

  /** Write a per-instance override record (fresh uid, rule stripped). */
  const writeOverride = async (occurrence, fields) => {
    const saved = await saveEvent({
      ...fields,
      uid: null, // fresh — overrides are their own records (LWW isolation)
      created_at: null,
      recurrence: null,
      exdates: [],
      is_occurrence: undefined,
      occurrence_date: undefined,
      calendar: occurrence.calendar || 'personal',
      override_of: {
        master_uid: occurrence.uid,
        occurrence_date: occurrence.occurrence_date,
      },
    });
    bumpCalendarEvents();
    return saved;
  };

  /** "All events": rewrite the master — times re-anchored to the series'
   *  first day (wall clock preserved), overrides + exdates KEPT. */
  const applyEditToMaster = async (occurrence, fields) => {
    const master = masterOf(occurrence);
    if (!master) return null;
    const next = {
      ...master,
      title: fields.title,
      location: fields.location,
      description: fields.description,
      calendar: fields.calendar || master.calendar,
      reminders: fields.reminders,
      recurrence: fields.recurrence ?? master.recurrence,
      // exdates deliberately KEPT (documented dialog subcopy).
    };
    if (fields.all_day) {
      const spanDays = Math.max(
        0,
        Math.round(
          (startOfDay(new Date(fields.end_date + 'T00:00:00')) -
            startOfDay(new Date(fields.start_date + 'T00:00:00'))) /
            86_400_000
        )
      );
      next.all_day = true;
      next.start = null;
      next.end = null;
      next.start_date = master.all_day ? master.start_date : toDateKey(new Date(master.start));
      next.end_date = toDateKey(addDays(startOfDay(new Date(next.start_date + 'T00:00:00')), spanDays));
    } else {
      const masterAnchor = master.all_day
        ? startOfDay(new Date(master.start_date + 'T00:00:00'))
        : new Date(master.start);
      const newStart = dayAtMinutes(startOfDay(masterAnchor), minutesOfDay(new Date(fields.start)));
      const durationMs = Math.max(new Date(fields.end) - new Date(fields.start), 0);
      next.all_day = false;
      next.start_date = null;
      next.end_date = null;
      next.start = newStart.toISOString();
      next.end = new Date(newStart.getTime() + durationMs).toISOString();
    }
    const saved = await persistEdit(master, next);
    if (!saved) return null;
    bumpCalendarEvents();
    return saved;
  };

  /** "This event" delete: exdate on the master; an override's delete is the
   *  documented TWO writes (record delete + exdate — the base instance would
   *  otherwise resurface). */
  const deleteThisOccurrence = async (occurrence) => {
    const masterUid = occurrence.override_of?.master_uid || occurrence.uid;
    const occurrenceDate = occurrence.override_of?.occurrence_date || occurrence.occurrence_date;
    if (occurrence.override_of) {
      await deleteEvent(occurrence.uid, occurrence.calendar || 'personal');
      // P4-N1: if an invitation ever got attached to this override (the gate
      // escape this fold-in closes; historical data may carry them), cancel
      // it — the SAME best-effort sweep the normal delete uses. A plain
      // occurrence has no record uid of its own, so no invitation can exist
      // under its identity — master-level invites are blocked by the gate.
      if (isAuthenticated && !occurrence.origin && calendarTeamId(occurrence.calendar) == null) {
        cancelAppointment(occurrence.uid).catch(() => {});
      }
    }
    const master = events.find((e) => e.uid === masterUid && e.recurrence);
    if (master && occurrenceDate) {
      const exdates = Array.isArray(master.exdates) ? master.exdates : [];
      if (!exdates.includes(occurrenceDate)) {
        await saveEvent({ ...master, exdates: [...exdates, occurrenceDate] });
      }
    }
    bumpCalendarEvents();
  };

  const handleSaveDraft = async (fields, extra = {}) => {
    // Saving FROM an occurrence of a recurring master → the scope dialog
    // decides (This event → override record; All events → master rewrite).
    if (editorDraft?.is_occurrence && masterOf(editorDraft)) {
      setEditorDraft(null);
      setPopover(null);
      setScopeDialog({ mode: 'edit', occurrence: editorDraft, fields, extra });
      return null;
    }
    return handleSaveDraftDirect(fields, extra);
  };

  const handleSaveDraftDirect = async (fields, extra = {}) => {
    // Stamp the creator on NEW team events (PLAN §3.5 "creator shown").
    if (calendarTeamId(fields.calendar) != null && !fields.created_by && currentUser?.id != null) {
      fields = { ...fields, created_by: { id: currentUser.id, name: currentUser.name || null } };
    }
    // Plans v2: a new personal event past Free's cap opens the upgrade instead.
    if (
      !editorDraft?.uid &&
      (fields.calendar == null || fields.calendar === 'personal') &&
      !(await requireLocalCapacityFor('calendarEvents', countOwnEvents))
    ) {
      return null;
    }
    // A refused write leaves the editor open so the user can retry.
    const saved = await persistEdit(editorDraft, fields);
    if (!saved) return null;
    setEditorDraft(null);
    setPopover(null);
    bumpCalendarEvents();
    // Invitations (Phase 2): both calls go through the single service. A 402
    // opens the PlanUpgradeModal (polish round — the app-wide pattern); the
    // local save above already succeeded (free = local-only still works).
    const { success } = useNotificationStore.getState();
    try {
      // Order matters when both apply: PATCH the existing rows FIRST (server
      // bumps versions, resets responses on start/end change — Decision 6
      // lives server-side), then create the new rows so they are born with
      // the current snapshot and are not double-notified by the PATCH.
      if (extra.hasInvitations) {
        await updateAppointment(saved.uid, buildSnapshotFromEvent(saved));
      }
      if (extra.invitees?.length > 0) {
        await createInvitations({
          appointmentUid: saved.uid,
          snapshot: buildSnapshotFromEvent(saved),
          userIds: extra.invitees.map((u) => u.id),
          recurring: invitesBlockedFor(saved),
        });
        success(t('calendar.invite.invitedN', { count: extra.invitees.length }));
      }
    } catch (err) {
      surfaceInviteError(err, t('calendar.invite.updateFailed'));
    }
    return saved;
  };

  const handleUpdateEventTimes = async (event, { start, end }) => {
    await saveEvent({
      ...event,
      all_day: false,
      start: start.toISOString(),
      end: end.toISOString(),
      start_date: null,
      end_date: null,
    });
    bumpCalendarEvents();
  };

  /** Route a delete: recurring occurrence/override → scope dialog first. */
  const requestDelete = (event) => {
    if ((event.is_occurrence && masterOf(event)) || event.override_of) {
      setPopover(null);
      setEditorDraft(null);
      setScopeDialog({ mode: 'delete', occurrence: event });
      return;
    }
    setDeleteTarget(event);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteEvent(deleteTarget.uid, deleteTarget.calendar || 'personal');
    // "All events": the master's override records go with it (client-side
    // sweep — overrides are ordinary records linked via data.override_of).
    if (deleteTarget.recurrence) {
      const overrides = events.filter((e) => e.override_of?.master_uid === deleteTarget.uid);
      for (const o of overrides) {
        // eslint-disable-next-line no-await-in-loop
        await deleteEvent(o.uid, o.calendar || 'personal').catch(() => {});
      }
    }
    // Organizer deleting an event they may have shared: cancel the
    // invitations too (DELETE is un-gated server-side — backend interp. 1 —
    // and idempotent). Best-effort: an event never shared 404s harmlessly,
    // and offline/free local deletes must never block on the network.
    // Team events carry no invitations in v1 (visibility IS membership).
    if (isAuthenticated && !deleteTarget.origin && calendarTeamId(deleteTarget.calendar) == null) {
      cancelAppointment(deleteTarget.uid).catch(() => {});
    }
    setDeleteTarget(null);
    setPopover(null);
    setEditorDraft(null);
    bumpCalendarEvents();
  };

  // Accept/Decline on a pending-invite ghost (grid popover surface) — the
  // same single service path the bell and the chat card use.
  const handleInviteResponse = async (ghostEvent, action) => {
    const invite = ghostEvent?.pending_invite;
    if (!invite?.token) return;
    const { success, info, error } = useNotificationStore.getState();
    try {
      await respondToInvitation(invite.token, action, invite.row);
      if (action === 'accept') success(t('calendar.invite.addedToCalendar'));
      else info(t('calendar.invite.declinedToast'));
      setPopover(null);
    } catch (err) {
      error(err?.response?.data?.error || err.message || t('calendar.invite.respondFailed'));
    }
  };

  const handleCreateDraftFromGrid = ({ start, end }) => {
    setEditorDraft({ start: start.toISOString(), end: end.toISOString() });
  };

  // .ics import/export (relocated from the removed sidebar; toolbar ⋯ menu).
  const handleImportIcs = async () => {
    if (icsBusy) return;
    setIcsBusy(true);
    try {
      const text = await pickIcsText();
      if (text == null) return;
      const { events: parsed, skipped } = parseIcs(text);
      const imported = await importEvents(parsed);
      bumpCalendarEvents();
      setImportReport({ imported, skipped });
    } catch (err) {
      // Plans v2: an import that does not fit says so with the upgrade.
      if (err?.planLimit) {
        setUpgradeReason(err.message);
        setUpgradeModalOpen(true);
        return;
      }
      useNotificationStore.getState().error(String(err?.message || err), t('calendar.toast.importFailed'));
    } finally {
      setIcsBusy(false);
    }
  };

  const handleExportIcs = async () => {
    if (icsBusy) return;
    setIcsBusy(true);
    try {
      const all = await listEvents();
      if (all.length === 0) {
        useNotificationStore
          .getState()
          .info(t('calendar.toast.nothingToExport'), t('calendar.exportDialogTitle'));
        return;
      }
      const saved = await saveIcsText(buildIcs(all));
      if (saved) {
        useNotificationStore
          .getState()
          .success(
            t('calendar.toast.exportedN', { count: all.length }),
            t('calendar.exportDialogTitle')
          );
      }
    } catch (err) {
      useNotificationStore.getState().error(String(err?.message || err), t('calendar.toast.exportFailed'));
    } finally {
      setIcsBusy(false);
    }
  };

  const handleQuickCreate = (day) => {
    const start = startOfDay(day);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60000);
    setEditorDraft({ start: start.toISOString(), end: end.toISOString() });
  };

  return {
    handleUpdateEventTimesGuarded,
    handleDuplicateEvent,
    writeOverride,
    applyEditToMaster,
    deleteThisOccurrence,
    handleSaveDraft,
    requestDelete,
    confirmDelete,
    handleInviteResponse,
    handleCreateDraftFromGrid,
    handleImportIcs,
    handleExportIcs,
    handleQuickCreate,
  };
}
